import { useMemo } from "react";
import StageSubmissionView from "./StageSubmissionView";
import { formatAssignmentStatus } from "../../utils/formatAssignmentStatus";
import { getCurrentStageIds } from "../../utils/currentStage";

const ACTIVE_STATUSES = ["PENDING", "IN_PROGRESS", "SUBMITTED"];

function ParallelIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="3" y1="7" x2="18" y2="7" />
      <polyline points="14 3 18 7 14 11" />
      <line x1="3" y1="17" x2="18" y2="17" />
      <polyline points="14 13 18 17 14 21" />
    </svg>
  );
}

function buildGroupedHistory(stageAssignments) {
  const grouped = new Map();
  const sorted = [...stageAssignments].sort(
    (a, b) => new Date(a.assignedAt) - new Date(b.assignedAt),
  );

  sorted.forEach((assignment) => {
    if (!grouped.has(assignment.stageId)) {
      grouped.set(assignment.stageId, {
        stageId: assignment.stageId,
        stageName: assignment.stageName,
        firstVisitedAt: assignment.assignedAt,
        visits: [],
      });
    }
    grouped.get(assignment.stageId).visits.push(assignment);
  });

  return Array.from(grouped.values())
    .map((group) => ({
      ...group,
      visits: [...group.visits].sort(
        (a, b) => (a.visitNumber || 1) - (b.visitNumber || 1),
      ),
    }))
    .sort((a, b) => new Date(a.firstVisitedAt) - new Date(b.firstVisitedAt));
}

function getStageCardState(stageGroup, isCurrentStage) {
  if (isCurrentStage) return "current";
  if (!stageGroup.visits.length) return "unvisited";
  if (
    stageGroup.visits.some((visit) => ACTIVE_STATUSES.includes(visit.status))
  ) {
    return "active";
  }
  if (stageGroup.visits.every((visit) => visit.status === "SKIPPED")) {
    return "skipped";
  }
  return "completed";
}

export default function StageTracker({ project }) {
  const stageAssignments = useMemo(
    () => project?.stageAssignments || [],
    [project?.stageAssignments],
  );
  const workflowStages = useMemo(
    () => project?.workflow?.definition?.stages || [],
    [project?.workflow?.definition?.stages],
  );
  const currentStageIds = getCurrentStageIds(project);

  const groupedHistory = useMemo(
    () => buildGroupedHistory(stageAssignments),
    [stageAssignments],
  );

  const stageOrderMap = useMemo(() => {
    const map = new Map();
    workflowStages.forEach((stage, index) => {
      map.set(stage.id, index + 1);
    });
    return map;
  }, [workflowStages]);

  const groupedWithUnvisited = useMemo(() => {
    if (!project?.showAllStages) {
      return [...groupedHistory].sort((a, b) => {
        const orderA = stageOrderMap.get(a.stageId) || Number.MAX_SAFE_INTEGER;
        const orderB = stageOrderMap.get(b.stageId) || Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) return orderA - orderB;
        return new Date(a.firstVisitedAt) - new Date(b.firstVisitedAt);
      });
    }

    const visitedMap = new Map(
      groupedHistory.map((group) => [group.stageId, group]),
    );
    return workflowStages.map((stage) => {
      return (
        visitedMap.get(stage.id) || {
          stageId: stage.id,
          stageName: stage.name,
          visits: [],
        }
      );
    });
  }, [groupedHistory, project?.showAllStages, stageOrderMap, workflowStages]);

  const activeCount = stageAssignments.filter((item) =>
    ACTIVE_STATUSES.includes(item.status),
  ).length;

  const isRunningInParallel = currentStageIds.length > 1;
  // Instructions written to each assignee, keyed by the assignment they were
  // aimed at so they render inside the matching visit row. The detail endpoint
  // already scopes these to the assignments this user is allowed to see.
  const instructionsByAssignment = useMemo(() => {
    const map = new Map();
    (project?.stageNotes || [])
      .filter(
        (note) => note.direction === "TO_ASSIGNEE" && note.stageAssignmentId,
      )
      .forEach((note) => {
        if (!map.has(note.stageAssignmentId)) {
          map.set(note.stageAssignmentId, []);
        }
        map.get(note.stageAssignmentId).push(note);
      });
    return map;
  }, [project?.stageNotes]);

  const currentStageNames = groupedWithUnvisited
    .filter((group) => currentStageIds.includes(group.stageId))
    .map((group) => group.stageName);

  return (
    <div className="stage-tracker">
      <div className="stage-tracker-header">
        <div>
          <h3>Workflow Stage Tracker</h3>
          <p className="small-muted">
            Each stage is shown separately with visits and submission history.
          </p>
        </div>
        <div className="stage-tracker-summary">
          <span className="summary-item">
            Stages: <strong>{groupedWithUnvisited.length}</strong>
          </span>
          <span className="summary-item">
            Active: <strong>{activeCount}</strong>
          </span>
          {isRunningInParallel && (
            <span className="summary-item summary-item--parallel">
              <ParallelIcon />
              Running in parallel: <strong>{currentStageIds.length}</strong>
            </span>
          )}
        </div>
      </div>

      {isRunningInParallel && (
        <div className="stage-parallel-banner">
          <ParallelIcon />
          <span>
            These stages are active at the same time —{" "}
            {currentStageNames.join(", ")} — and each must be completed before
            the workflow can move on.
          </span>
        </div>
      )}

      <div className="stage-tracker-list">
        {groupedWithUnvisited.map((stageGroup, index) => {
          const stageNumber =
            stageOrderMap.get(stageGroup.stageId) || index + 1;
          const isCurrentStage = currentStageIds.includes(stageGroup.stageId);
          const isParallelNow = isRunningInParallel && isCurrentStage;
          const cardState = getStageCardState(stageGroup, isCurrentStage);

          return (
            <div
              key={stageGroup.stageId}
              className={`stage-group stage-card stage-card--${cardState}${
                isParallelNow ? " stage-card--parallel" : ""
              }`}
            >
              <div className="stage-card-header">
                <span className="stage-index">{stageNumber}</span>
                <div className="stage-title">
                  <span className="stage-group-name">
                    {stageGroup.stageName}
                  </span>
                  {isParallelNow ? (
                    <span
                      className="stage-current stage-current--parallel"
                      title={`Running alongside: ${currentStageNames
                        .filter((n) => n !== stageGroup.stageName)
                        .join(", ")}`}
                    >
                      <ParallelIcon /> Parallel Now
                    </span>
                  ) : (
                    isCurrentStage && (
                      <span className="stage-current">Current Stage</span>
                    )
                  )}
                  {!stageGroup.visits.length && (
                    <span className="stage-pill stage-pill-muted">
                      Not Started
                    </span>
                  )}
                </div>
                <span className="visit-count-badge">
                  {stageGroup.visits.length} visit
                  {stageGroup.visits.length === 1 ? "" : "s"}
                </span>
              </div>

              {stageGroup.visits.length === 0 && (
                <div className="small-muted">No visits yet.</div>
              )}

              {stageGroup.visits.map((visit) => {
                const statusInfo = formatAssignmentStatus(visit.status);
                const nextStageName = groupedWithUnvisited.find(
                  (group) => group.stageId === visit.chosenNextStageId,
                )?.stageName;

                return (
                  <div key={visit.id} className="visit-row">
                    <div className="visit-detail">
                      <div className="visit-top-row">
                        <span className="visit-label">
                          {stageGroup.visits.length > 1
                            ? `Visit ${visit.visitNumber || 1}`
                            : "Visit"}
                        </span>
                        <span
                          className="visit-status-pill"
                          style={{
                            backgroundColor: statusInfo.bgColor,
                            color: statusInfo.textColor,
                          }}
                        >
                          {statusInfo.label}
                        </span>
                      </div>
                      <span className="visit-assignee">
                        {visit.assignee
                          ? `${visit.assignee.firstName} ${visit.assignee.lastName}`
                          : "Unassigned"}
                      </span>
                      {visit.completedAt && (
                        <span className="visit-completed">
                          Completed{" "}
                          {new Date(visit.completedAt).toLocaleDateString(
                            "en-IN",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                        </span>
                      )}
                      {(visit.chosenNextStageId || nextStageName) && (
                        <span className="visit-transition">
                          {visit.status === "RETURNED" ? "<-" : "->"}{" "}
                          {nextStageName || "Unassigned"}
                        </span>
                      )}
                      {visit.reviewNote && (
                        <div className="visit-note-inline">
                          Note: "{visit.reviewNote}"
                        </div>
                      )}
                      {(() => {
                        // All notes on a visit share one recipient, so the
                        // "to whom" is said once as a heading rather than
                        // repeated above every instruction body.
                        const instructions =
                          instructionsByAssignment.get(visit.id) || [];
                        if (!instructions.length) return null;
                        const recipient =
                          instructions[0].recipient?.firstName ||
                          visit.assignee?.firstName ||
                          "assignee";
                        return (
                          <div className="visit-instructions">
                            <div className="visit-instructions-head">
                              <span className="visit-instruction-tag">
                                {instructions.length === 1
                                  ? "Instruction"
                                  : `${instructions.length} instructions`}
                              </span>
                              <span className="visit-instruction-to">
                                to {recipient}
                              </span>
                            </div>
                            {instructions.map((note) => (
                              <div key={note.id} className="visit-instruction">
                                <div className="visit-instruction-body">
                                  {note.body}
                                </div>
                                <div className="visit-instruction-meta">
                                  {note.author?.firstName}{" "}
                                  {note.author?.lastName}
                                  {note.createdAt &&
                                    ` · ${new Date(
                                      note.createdAt,
                                    ).toLocaleDateString("en-IN", {
                                      day: "numeric",
                                      month: "short",
                                    })}`}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}

              {stageGroup.visits.some(
                (visit) => (visit.taskSubmissions || []).length > 0,
              ) && (
                <div className="stage-actions">
                  <StageSubmissionView
                    submissions={stageGroup.visits.flatMap(
                      (visit) => visit.taskSubmissions || [],
                    )}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!project?.showAllStages && groupedWithUnvisited.length === 0 && (
        <div className="small-muted">No stage is assigned to you yet.</div>
      )}
    </div>
  );
}
